import React from 'react';
import cn from 'classnames';
import { Button, ButtonProps } from '@odin-labs/components';
import { JobsiteInvitation, Localize } from 'containers/selfOnboarding/types';
import { Copy } from 'containers/selfOnboarding/steps/utils';
import { jobsiteDoesNotAllowToSkipSteps } from 'containers/selfOnboarding/helpers/utils';
import { ArrowRightIcon, ForwardIcon } from 'components/icons';

export type StepActionsProps = {
  // Optional: the authenticated flow's pre-account steps render before any
  // jobsite invitation is loaded. Only read via optional chaining below.
  jobsiteInvitation?: JobsiteInvitation;
  actions?: 'all' | 'continue' | 'skip';
  continueActionText?: string;
  continueActionCopy?: Copy;
  continueActionIcon?: ButtonProps['icon'];
  continueActionEnabled?: boolean;
  continueActionWithSpinner?: boolean;
  skipActionText?: string;
  skipActionCopy?: Copy;
  skipActionIcon?: ButtonProps['icon'];
  onContinue?: ButtonProps['onClick'];
  onSkip?: ButtonProps['onClick'];
  ignoreJobsiteConfigSkipFlag?: boolean;
  isCurrentStepRequired?: boolean;
  isReview?: boolean;
  showAcknowledgementText?: boolean;
  showSmsOptInAcknowledgement?: boolean;
  showSmsLiteAcknowledgement?: boolean;
  localize: Localize;
};

export const classes = {
  container: cn(
    'odin-my-6 odin-grid odin-grid-cols-12 odin-gap-x-6 odin-gap-y-3 print:odin-hidden',
  ),
  button: (isHidden: boolean, extraClasses: string): string =>
    cn(
      extraClasses,
      'odin-justify-center odin-col-span-full sm:odin-col-span-6',
      isHidden && 'odin-hidden',
    ),
  skipButton: 'odin-order-2 sm:odin-order-1',
  continueButton: 'odin-order-1 sm:odin-order-2 sm:odin-col-start-7',
  acknowledgementText: cn(
    'odin-text-sm odin-text-center odin-order-3',
    'odin-col-span-full sm:odin-col-span-6 sm:odin-col-start-7',
  ),
};

export function StepActions(props: StepActionsProps): React.ReactElement {
  const {
    jobsiteInvitation,
    isReview,
    localize,

    actions = 'all',
    onContinue,
    continueActionCopy = isReview ? Copy.save : Copy.continue,
    continueActionText = localize(continueActionCopy),
    continueActionIcon = isReview ? null : ArrowRightIcon,
    continueActionEnabled = true,
    continueActionWithSpinner = false,

    onSkip,
    skipActionCopy = isReview ? Copy.cancel : Copy.skip_for_now,
    skipActionText = localize(skipActionCopy),
    skipActionIcon = isReview ? null : ForwardIcon,

    ignoreJobsiteConfigSkipFlag,
    showAcknowledgementText,
    showSmsOptInAcknowledgement,
    showSmsLiteAcknowledgement,
    isCurrentStepRequired,
  } = props;

  const hideContinueAction = actions === 'skip';
  const hideSkipAction =
    actions === 'continue' ||
    (!ignoreJobsiteConfigSkipFlag &&
      !isReview &&
      (isCurrentStepRequired ||
        jobsiteDoesNotAllowToSkipSteps(
          jobsiteInvitation?.jobsiteContractor.jobsite.modules,
        )));

  return (
    <div className={classes.container}>
      <Button
        onClick={onSkip}
        theme="white"
        size="lg"
        text={skipActionText}
        icon={skipActionIcon}
        className={classes.button(hideSkipAction, classes.skipButton)}
      />
      <Button
        onClick={onContinue}
        type="submit"
        size="lg"
        text={continueActionText}
        icon={continueActionIcon}
        className={classes.button(hideContinueAction, classes.continueButton)}
        disabled={!continueActionEnabled}
        withSpinner={continueActionWithSpinner}
      />
      {showAcknowledgementText && (
        <div className={classes.acknowledgementText}>
          {localize(
            showSmsOptInAcknowledgement
              ? Copy.self_onboarding_acknowledgment_text_sms_opt_in_prefix
              : Copy.self_onboarding_acknowledgment_text_by_clicking_continue,
          )}
          <a
            href="https://www.useodin.com/terms-of-use"
            target="_blank"
            rel="noreferrer"
          >
            {localize(Copy.self_onboarding_acknowledgment_text_terms_of_use)}
          </a>
          {localize(Copy.self_onboarding_acknowledgment_text_and)}
          <a
            href="https://www.useodin.com/privacy-policy"
            target="_blank"
            rel="noreferrer"
          >
            {localize(Copy.self_onboarding_acknowledgment_text_privacy_policy)}
          </a>
          {showSmsOptInAcknowledgement &&
            localize(
              Copy.self_onboarding_acknowledgment_text_sms_opt_in_suffix,
            )}
        </div>
      )}
      {showSmsLiteAcknowledgement && (
        <div className={classes.acknowledgementText}>
          {localize(Copy.self_onboarding_acknowledgment_text_sms_opt_in_lite)}
        </div>
      )}
    </div>
  );
}
